import React, { useEffect } from 'react';
import { Box, Flex, Text, Badge, HStack } from '@chakra-ui/react';
import { CricketScore, Competitor } from '../../types/types';
import { debugCricketScores } from '../../utils/debug';

interface CricketScoreSummaryProps {
  cricketScores: CricketScore[];
  firstCompetitor: Competitor;
  secondCompetitor: Competitor;
  compact?: boolean; // Whether to use compact display for header
}

/**
 * Displays a summary of cricket scores in the format: Team A Runs/Wickets : Team B Runs/Wickets
 */
export const CricketScoreSummary: React.FC<CricketScoreSummaryProps> = ({ 
  cricketScores, 
  firstCompetitor, 
  secondCompetitor,
  compact = false
}) => {
  // Debug cricket scores
  useEffect(() => {
    debugCricketScores(cricketScores, 'CricketScoreSummary');
  }, [cricketScores]);

  // If no cricket scores are available, don't render anything
  if (!cricketScores || cricketScores.length === 0) {
    console.log('CricketScoreSummary: No cricket scores to display');
    return null;
  }

  // Get the latest cricket score (highest sequence ID)
  const latestScore = cricketScores.reduce((latest, current) => 
    current.sequenceId > latest.sequenceId ? current : latest, 
    cricketScores[0]
  );

  // Debug the latest score
  console.log('Latest cricket score:', latestScore);
  
  // Check if team scores exist and have required properties
  const { firstTeamScore, secondTeamScore } = latestScore;
  
  if (!firstTeamScore || !secondTeamScore) {
    console.error('Missing team scores in cricket data');
    return null;
  }
  
  // Check if required properties exist
  const hasRequiredProps = 
    firstTeamScore.runs !== undefined && 
    firstTeamScore.wickets !== undefined && 
    firstTeamScore.isBatting !== undefined && 
    secondTeamScore.runs !== undefined && 
    secondTeamScore.wickets !== undefined && 
    secondTeamScore.isBatting !== undefined;
  
  if (!hasRequiredProps) {
    console.error('Missing required properties in team scores', { firstTeamScore, secondTeamScore });
    return null;
  }

  if (compact) {
    // Compact version for header
    return (
      <HStack spacing={2} ml={2}>
        <Badge colorScheme="blue" fontSize="xs" px={1}>
          {firstCompetitor.name.substring(0, 3).toUpperCase()}
        </Badge>
        <Text fontSize="sm" fontWeight={firstTeamScore.isBatting ? "bold" : "normal"}>
          {firstTeamScore.runs}/{firstTeamScore.wickets}
          {firstTeamScore.isBatting && <Badge colorScheme="green" size="xs" ml={1}>*</Badge>}
        </Text>
        <Text fontSize="xs" color="gray.500">vs</Text>
        <Text fontSize="sm" fontWeight={secondTeamScore.isBatting ? "bold" : "normal"}>
          {secondTeamScore.runs}/{secondTeamScore.wickets}
          {secondTeamScore.isBatting && <Badge colorScheme="green" size="xs" ml={1}>*</Badge>}
        </Text>
        <Badge colorScheme="blue" fontSize="xs" px={1}>
          {secondCompetitor.name.substring(0, 3).toUpperCase()}
        </Badge>
      </HStack>
    );
  }
  
  // Standard version
  return (
    <Box mt={2}>
      <Flex direction="row" justify="space-between" align="center">
        {/* First Team Score */}
        <Flex direction="column" align="center" flex={1}>
          <Text fontWeight="bold" fontSize="sm">{firstCompetitor.name}</Text>
          <Flex align="center">
            <Text fontSize="xl" fontWeight="bold">{firstTeamScore.runs}</Text>
            <Text fontSize="md" ml={1}>/{firstTeamScore.wickets}</Text>
            {firstTeamScore.isBatting && <Badge colorScheme="green" ml={2}>Batting</Badge>}
          </Flex>
        </Flex>
        
        {/* Separator */}
        <Text mx={4} fontSize="xl" color="gray.400">vs</Text>
        
        {/* Second Team Score */}
        <Flex direction="column" align="center" flex={1}>
          <Text fontWeight="bold" fontSize="sm">{secondCompetitor.name}</Text>
          <Flex align="center">
            <Text fontSize="xl" fontWeight="bold">{secondTeamScore.runs}</Text>
            <Text fontSize="md" ml={1}>/{secondTeamScore.wickets}</Text>
            {secondTeamScore.isBatting && <Badge colorScheme="green" ml={2}>Batting</Badge>}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default CricketScoreSummary;
